import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { StockAdjustInput } from '@buddys-choice/shared'
import type { StockAdjustInput as StockAdjustInputType } from '@buddys-choice/shared'
import { apiGet, apiPost } from '../../lib/api'

interface StockLookupItem {
  stock_kind: 'variant' | 'product' | 'combo'
  variant_id: string | null
  product_id: string | null
  combo_id: string | null
  sku: string
  product_name: string
  stock_qty: number
}

export const AdminStockAdjustPage: React.FC = () => {
  const { variantId: targetId } = useParams({ from: '/admin/stock/$variantId/adjust' })
  const { change: initialChange } = useSearch({ from: '/admin/stock/$variantId/adjust' })
  const navigate = useNavigate()

  const [item, setItem] = useState<StockLookupItem | null>(null)
  const [loadError, setLoadError] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitError, setSubmitError] = useState('')

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<StockAdjustInputType>({
    resolver: zodResolver(StockAdjustInput),
    defaultValues: {
      adjustmentType: 'manual_adjustment',
      quantityChange: initialChange ?? 5,
      reason: '',
    },
  })

  useEffect(() => {
    Promise.any([
      apiGet<StockLookupItem>('stock/lookup', { variantId: targetId }),
      apiGet<StockLookupItem>('stock/lookup', { productId: targetId }),
      apiGet<StockLookupItem>('stock/lookup', { comboId: targetId }),
    ])
      .then((found) => {
        setItem(found)
        if (found.stock_kind === 'product' && found.product_id) {
          setValue('productId', found.product_id)
        } else if (found.stock_kind === 'combo' && found.combo_id) {
          setValue('comboId', found.combo_id)
        } else if (found.variant_id) {
          setValue('variantId', found.variant_id)
        }

        if (initialChange === undefined) {
          setValue('reason', 'Restocked from workshop')
        } else if (initialChange < 0) {
          setValue('reason', 'Manual audit inventory correction')
        }
      })
      .catch((err: Error) => setLoadError(err.message || 'Failed to load stock item.'))
      .finally(() => setLoading(false))
  }, [targetId, initialChange, setValue])

  const changeAmount = watch('quantityChange')

  const handleConfirmAdjustment = async (data: StockAdjustInputType) => {
    setSubmitError('')
    try {
      await apiPost('stock/adjust', { ...data, reason: data.reason.trim() })
      navigate({ to: '/admin/stock' })
    } catch (err: unknown) {
      setSubmitError((err as Error).message || 'Stock adjustment failed.')
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <span className="spinner text-[var(--gold-dark)]" />
      </div>
    )
  }

  if (loadError || !item) {
    return (
      <div className="space-y-4">
        <Link to="/admin/stock" className="inline-flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--gold-main)]">
          <ArrowLeft className="w-4 h-4" /> Back to stock
        </Link>
        <div className="alert alert-error text-sm">{loadError || 'Item not found.'}</div>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-md">
      <Link to="/admin/stock" className="inline-flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--gold-main)]">
        <ArrowLeft className="w-4 h-4" /> Back to stock
      </Link>

      <div className="glass-card p-6">
        <h1 className="text-xl font-serif font-bold mb-1">Adjust stock quantity</h1>
        <p className="text-xs text-[var(--gold-dark)] font-mono mb-1">SKU: {item.sku}</p>
        <p className="text-xs text-[var(--text-muted)] mb-6">{item.product_name}</p>

        {submitError && <div className="alert alert-error text-xs mb-4">{submitError}</div>}

        <form onSubmit={handleSubmit(handleConfirmAdjustment)} className="space-y-4 text-sm">
          <div className="form-group">
            <label className="form-label">Quantity change (+ or −) *</label>
            <input
              type="number"
              {...register('quantityChange', { valueAsNumber: true })}
              aria-invalid={!!errors.quantityChange}
              className="form-input font-mono text-base"
            />
            {errors.quantityChange && <span className="form-error">{errors.quantityChange.message}</span>}
            <span className="text-[10px] text-[var(--text-muted)]">
              Current: {item.stock_qty} → New: {item.stock_qty + (changeAmount || 0)}
            </span>
          </div>

          <div className="form-group">
            <label className="form-label">Mandatory audit reason *</label>
            <textarea
              {...register('reason')}
              rows={4}
              placeholder="e.g. Received new stock batch from Coimbatore artisan."
              className="form-input"
              aria-invalid={!!errors.reason}
            />
            {errors.reason && <span className="form-error">{errors.reason.message}</span>}
          </div>

          <div className="flex gap-3 pt-2">
            <Link to="/admin/stock" className="btn btn-secondary flex-1 text-center">
              Cancel
            </Link>
            <button type="submit" disabled={isSubmitting} className="btn btn-primary flex-1 font-semibold">
              {isSubmitting ? <span className="spinner" /> : 'Confirm adjustment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
